import React, { useState, useLayoutEffect } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { getLocationList, googleAutocompleteApi } from '../../services/api/search.service';
import { CommonStyles, CommonProperties, Color } from '../../styles';
import Icon from '../Icon';
import BaseText from '../BaseText';
import to from '../../utils/to';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../../i18n';
import InputForm from '../InputForm';
import RowView from '../RowView';
import { useDispatch, useSelector } from 'react-redux';
import { selectTezEvent } from '../../store/selectors/events.selector';
import { EVENTS_ENABLED } from '../../config/env';
import { setExpenseValue } from '../../store/actions/expense';

const iconCategoryMap = {
  A: 'plane',
  H: 'hotel',
  C: 'mark',
  R: 'rail',
  G: 'mark',
  // GR: 'mark',
  // CR: 'car'
};

const initEnabledCategories = (source, hasAir, hasCar) => {
  const enabled = {
    airports: true,
    hotelGroups: true,
    cities: true,
  };
  // from locations for air searches always need to be airports
  if (source === 'from' && hasAir) {
    enabled.hotelGroups = false;
    enabled.cities = false;
  }

  // disabling non airport results for car currently; there is a problem with the api doing purchases for non airport searches
  if (hasCar) {
    enabled.hotelGroups = true;
    enabled.cities = true;
  }
  return enabled;
};

const initListScope = (hasAir, hasHotel, hasCar, hasRail) => {
  const result = {
    type: 'all',
    order: 'anycomponent',
  };
  if (hasRail) {
    result.type = 'rail';
    result.order = 'onlyrail';
    return result;
  }

  if (!hasHotel) {
    result.order = 'nohotel';
  }

  return result;
};

const getAutoCompleteTitle = (hasAir, hasCar, hasHotel, hasRail) => {
  if (hasRail) {
    return 'RailTitle';
  } else if (hasAir && !hasCar && !hasHotel) {
    return 'AirTitle';
  } else {
    return 'DefaultTitle';
  }
};

const StationAutoCompleteModal = ({ navigation, route }) => {
  const { t } = useTranslation(i18nNS.searchWidget);
  const { tKey } = useComponentTKey('StationAutoCompleteModal');
  const dispatch = useDispatch();
  const {
    hasHotel,
    hasAir,
    hasRail,
    hasCar,
    source,
    index,
    subSearchType,
    returnScreen,
    inputName,
    dominantSearchType,
    isExpenseWidget = false,
    actionType = null,
    subTab = null,
  } = route.params || {};
  const [searchInput, setSearchInput] = useState('');
  const eventData = useSelector(selectTezEvent);

  const [options, setOptions] = useState(() => {
    if (eventData === undefined) {
      return [];
    }
    //events have default options
    if (EVENTS_ENABLED && dominantSearchType === 'car' && source === 'from') {
      const list = [];
      const airports = eventData.AirportCd.split(',');
      airports.map((airport) => list.push({ label: airport, category: airport }));
      return list;
    }
    if (EVENTS_ENABLED && source === 'to') {
      const list = [];
      const airports = eventData.AirportCd.split(',');
      airports.map((airport) => list.push({ label: airport, category: airport }));
      return list;
    }
    return [{}];
  });
  const [listLoading, setListLoading] = useState(false);
  const [listResponse, setListResponse] = useState({});
  const [showCategories] = useState(() => initEnabledCategories(source, hasAir, hasCar));
  const [listMetadata] = useState(() => initListScope(hasAir, hasHotel, hasCar, hasRail));
  const carOnly = () => hasCar && !hasAir && !hasHotel;
  // const allowGoogleSearch = () => (hasHotel && !hasAir && !hasCar) || carOnly();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: t(tKey(getAutoCompleteTitle(hasAir, hasHotel, hasCar, hasRail))),
      headerTitleAlign: 'center',
    });
  }, [navigation, t, tKey, hasAir, hasHotel, hasCar, hasRail]);

  // these methods currently mutate the array directly, probably not the best
  const addAirportDataToAutoComplete = (results, airports, searchTerm = '', altSearchTerm = '') => {
    let airResultLimit = 0;
    let regid = '';
    if (airports.length > 0) {
      altSearchTerm = altSearchTerm.toUpperCase();
      for (let i = 0; i < airports.length; i++) {
        if (airResultLimit < 5) {
          if (searchTerm || altSearchTerm) {
            const airportDisplay = airports[i].display.toUpperCase();
            const airpotyCityName = airports[i].cityName.toUpperCase();
            if (
              airports[i].IsParent == 1 &&
              (airportDisplay.indexOf(searchTerm) > -1 ||
                airportDisplay.indexOf(altSearchTerm) > -1 ||
                airpotyCityName.indexOf(searchTerm) > -1 ||
                airpotyCityName.indexOf(altSearchTerm) > -1)
            ) {
              regid = airports[i].RegionId;
            }

            if (airports[i].IsParent == 0 || airports[i].IsParent == 1) {
              if (airports[i].RegionId == 0) {
                //these added because, sometimes non searched word was appearing in search box.
                if (airports[i].RegionId == regid) {
                  if (
                    airportDisplay.indexOf(altSearchTerm) > -1 ||
                    airpotyCityName.indexOf(altSearchTerm) > -1
                  ) {
                    results.push({
                      displaylabel: airports[i].display,
                      label: airports[i].display,
                      category: 'A',
                      key: airports[i].IsParent,
                      regionid: airports[i].RegionId,
                      placeId: '0',
                    });
                    if (airports[i].IsParent == 1) {
                      airResultLimit++;
                    }
                  }
                }
              } else {
                if (airports[i].RegionId == regid && airports[i].RegionId > 0) {
                  results.push({
                    displaylabel: airports[i].display,
                    label: airports[i].display,
                    category: 'A',
                    key: airports[i].IsParent,
                    regionid: airports[i].RegionId,
                    placeId: '0',
                  });
                  if (airports[i].IsParent == 1) {
                    airResultLimit++;
                  }
                }
              }
            }
          } else {
            results.push({
              displaylabel: airports[i].display,
              label: airports[i].display,
              category: 'A',
              key: +airports[i].IsParent,
              regionid: airports[i].RegionId,
              placeId: '0',
            });
            if (airports[i].IsParent == 1) {
              airResultLimit++;
            }
          }
        } else {
          break;
        }
      }
    }
  };

  const addRailDataToAutoComplete = (
    results,
    railLocations,
    searchTerm = '',
    altSearchTerm = ''
  ) => {
    let railResultLimit = 0;
    if (railLocations.length > 0) {
      for (let i = 0; i < railLocations.length; i++) {
        if (railResultLimit <= 10) {
          if (searchTerm || altSearchTerm) {
            const railDisplay = railLocations[i].display.toUpperCase();
            if (railDisplay.indexOf(searchTerm) > -1 || railDisplay.indexOf(altSearchTerm) > -1) {
              results.push({
                displaylabel: railLocations[i].display,
                label: railLocations[i].display,
                category: 'R',
                key: 1,
                regionid: 0,
              });
              railResultLimit++;
            }
          }
          results.push({
            displaylabel: railLocations[i].display,
            label: railLocations[i].display,
            category: 'R',
            key: 1,
            regionid: 0,
          });
          railResultLimit++;
        } else {
          break;
        }
      }
    }
  };

  const addHotelDataToAutoComplete = (
    results,
    hotelLocations,
    searchTerm = '',
    altSearchTerm = ''
  ) => {
    let hotelCount = 0;
    if (showCategories.hotelGroups && hotelLocations.length > 0) {
      for (let i = 0; i < hotelLocations.length; i++) {
        if (hotelCount < 10) {
          if (searchTerm || altSearchTerm) {
            const hotelDisplay = hotelLocations[i].display.toUpperCase();
            const hotelCityName = hotelLocations[i].cityName.toUpperCase();
            const hotelFriendlyName = hotelLocations[i].friendlyName.toUpperCase();
            if (
              hotelDisplay.indexOf(searchTerm) > -1 ||
              hotelDisplay.indexOf(altSearchTerm) > -1 ||
              hotelCityName.indexOf(searchTerm) > -1 ||
              hotelCityName.indexOf(altSearchTerm) > -1 ||
              hotelFriendlyName.indexOf(searchTerm) > -1 ||
              hotelFriendlyName.indexOf(altSearchTerm) > -1
            ) {
              results.push({
                displaylabel: hotelLocations[i].display,
                label: hotelLocations[i].display,
                category: 'H',
                key: '1',
                regionid: 0,
              });
              hotelCount++;
            }
          } else {
            results.push({
              displaylabel: hotelLocations[i].display,
              label: hotelLocations[i].display,
              category: 'H',
              key: '1',
              regionid: 0,
            });
            hotelCount++;
          }
        } else {
          break;
        }
      }
    }
    return hotelCount;
  };

  const addCityDataToAutoComplete = (
    results,
    cityLocations,
    hotelResultCount,
    searchTerm = '',
    altSearchTerm = ''
  ) => {
    const cityResultLimit = 10 - hotelResultCount;
    let cityResultCount = 0;
    if (showCategories.cities && cityResultLimit > 0 && cityLocations.length > 0) {
      for (let i = 0; i < cityLocations.length; i++) {
        if (cityResultCount < cityResultLimit) {
          if (searchTerm || altSearchTerm) {
            const cityDisplay = cityLocations[i].display.toUpperCase();
            if (cityDisplay.indexOf(searchTerm) > -1 || cityDisplay.indexOf(altSearchTerm) > -1) {
              results.push({
                displaylabel: cityLocations[i].display,
                label: cityLocations[i].display,
                category: 'C',
                key: '1',
                regionid: 0,
              });
              cityResultCount++;
            }
          } else {
            results.push({
              displaylabel: cityLocations[i].display,
              label: cityLocations[i].display,
              category: 'C',
              key: '1',
              regionid: 0,
            });
            cityResultCount++;
          }
        } else {
          break;
        }
      }
    }
  };

  const prepareData = (locationResponse) => {
    const initialResults = getInitialComponentwiseData(locationResponse);
    setComponentWiseData(initialResults, true);
  };

  const getFilteredComponentwiseData = (searchText, locationResponse) => {
    const results = [];
    if (locationResponse?.components) {
      let hotelCount = 0;
      const searchTerm = searchText.toUpperCase();
      //not sure what the point of altSearchTerm is. in the filters we are always toUpperCasing, so unless we toUpper this i don't think it will match
      let altSearchTerm = searchText;
      if (altSearchTerm.charAt(2) == ' ') {
        altSearchTerm =
          altSearchTerm.substr(0, 2) + '. ' + altSearchTerm.substr(3, altSearchTerm.length);
      } else if (altSearchTerm.charAt(2) == '.') {
        altSearchTerm = altSearchTerm.substr(0, 2) + altSearchTerm.substr(3, altSearchTerm.length);
      }
      switch (listMetadata.order) {
        case 'anycomponent':
          hotelCount = addHotelDataToAutoComplete(
            results,
            locationResponse.components[0].hotels,
            searchTerm,
            altSearchTerm
          );
          addCityDataToAutoComplete(
            results,
            locationResponse.components[1].cities,
            hotelCount,
            searchTerm,
            altSearchTerm
          );
          addAirportDataToAutoComplete(
            results,
            locationResponse.components[2].airports,
            searchTerm,
            altSearchTerm
          );
          break;
        case 'nohotel':
          addAirportDataToAutoComplete(
            results,
            locationResponse.components[2].airports,
            searchTerm,
            altSearchTerm
          );
          hotelCount = addHotelDataToAutoComplete(
            results,
            locationResponse.components[0].hotels,
            searchTerm,
            altSearchTerm
          );
          addCityDataToAutoComplete(
            results,
            locationResponse.components[1].cities,
            hotelCount,
            searchTerm,
            altSearchTerm
          );
          break;
        case 'onlyrail':
          addRailDataToAutoComplete(
            results,
            locationResponse.components[3].rail,
            searchTerm,
            altSearchTerm
          );
          break;
        default:
          break;
      }
    }
    if (results.length > 0) {
      return results;
    }
  };

  const getInitialComponentwiseData = (locationResponse) => {
    const results = [];
    let hotelCount = 0;
    switch (listMetadata.order) {
      case 'anycomponent':
        if (hasAir && source === 'from') {
          addAirportDataToAutoComplete(results, locationResponse.components[2].airports);
        } else {
          hotelCount = addHotelDataToAutoComplete(results, locationResponse.components[0].hotels);
          addCityDataToAutoComplete(results, locationResponse.components[1].cities, hotelCount);
          addAirportDataToAutoComplete(results, locationResponse.components[2].airports);
        }
        break;
      case 'nohotel':
        addAirportDataToAutoComplete(results, locationResponse.components[2].airports);
        // hotelCount = addHotelDataToAutoComplete(results, locationResponse.components[0].hotels);
        addCityDataToAutoComplete(results, locationResponse.components[1].cities, hotelCount);
        break;
      case 'onlyrail':
        addRailDataToAutoComplete(results, locationResponse.components[3].rail);
        break;
    }
    return results;
  };

  const setComponentWiseData = (results, checkForResults) => {
    let bindAutocomplete = false;
    if (checkForResults) {
      if (!results.length) {
        bindAutocomplete = true; // make false once we go get more data
        // TODO GO GET MORE DATA
        // if (($scope.searchModel.hasAir == false && $scope.searchModel.hasCar == true && $scope.searchModel.hasHotel == false)) {
        //     $scope.getCarAutocompleteData(source)
        // } else if ($scope.allowGoogleSearch && $scope.isGoogleApiLoaded) {
        //     $scope.GetGooglePredictions($scope.airport.cityName);
        // }
      } else {
        bindAutocomplete = true;
      }
    } else {
      bindAutocomplete = true;
    }
    if (bindAutocomplete) {
      if (results.length) {
        setOptions(results);
      } else {
        setOptions([{}]);
      }
    }
  };

  const handleChangeText = async (inputText) => {
    if (inputText) {
      const oldLength = searchInput.length;
      let searchText = hasHotel ? inputText : inputText.trim();
      setSearchInput(inputText);

      if (searchText.length === 3 && oldLength !== 4) {
        searchText = searchText.toLowerCase();
        setListLoading(true);
        const [, locationList] = await to(getLocationList(searchText, listMetadata.type));
        setListLoading(false);
        if (locationList) {
          setListResponse(locationList);
          prepareData(locationList);
        } else {
          // only car , then Search in Car Location
          if (carOnly()) {
            getCarLocationList(searchText); // for Car
          } else {
            if (!hasAir) {
              getGooglePredictions(searchText); // for Hotel
            }
          }
        }
      } else if (searchText.length > 3) {
        let filteredResults = [];
        if (listResponse) {
          filteredResults = getFilteredComponentwiseData(searchText, listResponse);
          //  setComponentWiseData(results, true);
        }
        if (carOnly()) {
          getCarLocationList(searchText, filteredResults); // for Car
        } else {
          if (!hasAir) {
            getGooglePredictions(searchText); // for Hotel
          }
        }
      }
    } else {
      clearInput();
    }
  };

  const clearInput = () => {
    setSearchInput('');
    setOptions([{}]);
  };

  const getCarLocationList = async (searchText, filteredResults) => {
    const carSpecificlist = new Array();

    // Get the Filtered locations if any present
    if (filteredResults) {
      for (let i = 0; i < filteredResults.length; i++) {
        carSpecificlist.push({
          displaylabel: filteredResults[i]['displaylabel'],
          label: filteredResults[i]['label'],
          category: filteredResults[i]['category'],
          key: filteredResults[i]['IsParent'],
          regionid: filteredResults[i]['regionid'],
          placeId: 0,
        });
      }
    } else {
      getGooglePredictions(searchText);
    }
  };

  const getGooglePredictions = async (input) => {
    try {
      const googleList = new Array();
      const googleListApi = await googleAutocompleteApi(input);
      googleListApi.predictions.forEach(function (value) {
        googleList.push({
          displaylabel: value.description,
          label: value.description,
          category: 'G',
          key: 0,
          regionid: 0,
          placeId: value.place_id,
        });
      });

      setOptions(googleList);
    } catch (err) {
      console.log(err);
    }
  };

  //   const getCarAutocompleteData = async (input) => {
  //     const [, carLocationList] = await to(getCarAutocompleteList(input));
  //     if (carLocationList) {
  //       //console.log(carLocationList);
  //       //         $.each(res , function(i,v){
  //       //             var newText = String(v.CarData).replace(new RegExp($scope.airport.cityName, "gi"), "<span class='bold'>$&</span>");
  //       //             $scope.dataAutoComplete.push({ displaylabel: newText, label: v.CarData, category: 'CR', key: v, regionid: 0, placeId : v });
  //       //         })
  //       //         setComponentWiseData($scope.source, false);
  //     } else if (allowGoogleSearch()) {
  //       //getGooglePredictions(input);
  //     }
  //   };

  // googleAutocomplete should only be done for source === 'to'
  /*
       const getGooglePredictions = async (input) => {
       const test = await GooglePlacesService.search(input);
       //console.log(test);
        var displaySuggestions = function(predictions, status) {
            if (status != google.maps.places.PlacesServiceStatus.OK) {
              return;
            }
            var newSearchText = $scope.airport.cityName;
            predictions.forEach(function(prediction) {
                var newText = String(prediction.description).replace(new RegExp(newSearchText, "gi"), "<span class='bold'>$&</span>");
                $scope.dataAutoComplete.push({ displaylabel: newText, label: prediction.description, category: 'GL', key: 0, regionid: 0, placeId : prediction.place_id });
            });
            setComponentWiseData($scope.source, false);
        }
     };
   */
  const handleOptionPress = (item) => {
    if (isExpenseWidget) {
      dispatch(
        setExpenseValue({
          actionType,
          subTab,
          key: inputName,
          value: item.displaylabel || item.label,
        })
      );
      navigation.goBack();
      return;
    }
    const shouldUpdateCategory =
      source === 'to' || (source === 'from' && hasCar && subSearchType === 'roundtrip');
    navigation.navigate(returnScreen, {
      selectedLocation: {
        ...item,
        source: source,
        criteriaIndex: index,
        componentType: listMetadata.type,
        shouldUpdateCategory,
        inputName,
      },
    });
  };

  const ListFooterComponent = options.length ? (
    <View style={styles.listFooter}>
      <BaseText>{t(tKey('EnterMore'))}...</BaseText>
    </View>
  ) : null;

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : ''}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 110 : 0}
      style={CommonStyles.flex1}
    >
      <View style={CommonStyles.flex1}>
        <InputForm
          autoFocus
          onChangeText={handleChangeText}
          style={styles.autocompleteTextInput}
          value={searchInput}
          disabled={
            (EVENTS_ENABLED && subSearchType !== 'oneway' && source === 'to') ||
            (EVENTS_ENABLED && dominantSearchType === 'car' && source === 'from')
              ? true
              : false
          }
          rightIcon={
            <RowView>
              {listLoading && (
                <ActivityIndicator key="loading" color={Color.active} style={CommonStyles.mr1} />
              )}
            </RowView>
          }
          rightIconContainerStyle={CommonStyles.mr1}
        />
        <View style={CommonStyles.flex1}>
          <FlatList
            data={options}
            keyExtractor={(item) => item.label + item.category + item.key + item.regionid}
            ListFooterComponent={() =>
              EVENTS_ENABLED && source === 'to' ? <></> : ListFooterComponent
            }
            renderItem={({ item }) => (
              <TouchableOpacity onPress={() => handleOptionPress(item)}>
                <View
                  style={[
                    styles.listOption,
                    item.category === 'A' && item.key === 0 ? CommonStyles.ml5 : null,
                  ]}
                >
                  {!!iconCategoryMap[item.category] && (
                    <Icon
                      color="black"
                      size={24}
                      name={iconCategoryMap[item.category]}
                      style={CommonStyles.mr2}
                    />
                  )}
                  <BaseText style={styles.optionText}>{item.label}</BaseText>
                </View>
              </TouchableOpacity>
            )}
          />
        </View>
      </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  autocompleteTextInput: {
    backgroundColor: Color.background,
    ...CommonProperties.h3,
    ...CommonProperties.my3,
    ...CommonProperties.mx1,
  },
  listOption: {
    flexDirection: 'row',
    alignItems: 'center',
    ...CommonProperties.p2,
  },
  listFooter: {
    borderTopColor: Color.black,
    borderTopWidth: 1,
    ...CommonProperties.m3,
  },
});

export default StationAutoCompleteModal;
